import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { useCart } from '../../contexts/CartContext';
import { calculateFeeBreakdown } from '../../utils/feeConstants';
import CartItem from './CartItem';
import CheckoutButtons from './CheckoutButtons';
import SellerGroup from './SellerGroup';
import MultiSellerCheckout from './MultiSellerCheckout';

// Format price with currency
const formatPrice = (price, currency = 'USD') => {
  if (price === null || price === undefined) return 'Price not available';
  if (price === 0) return 'Free';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(price);
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(11, 15, 26, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(12px);
  isolation: isolate;
  padding: 2rem;
  box-sizing: border-box;
  cursor: pointer;

  /* Add a subtle hint that clicking outside closes the modal */
  &::before {
    content: "Click outside to close";
    position: absolute;
    bottom: 1rem;
    left: 50%;
    transform: translateX(-50%);
    color: ${props => props.theme.colors.textDim};
    font-size: 0.75rem;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const ModalContent = styled.div`
  background: ${props => props.theme.colors.bg};
  border-radius: ${props => props.theme.radii.lg};
  width: 100%;
  max-width: 900px;
  max-height: calc(100vh - 2rem);
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.glowPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  position: relative;
  animation: modalSlideIn 0.3s ease-out;
  margin: auto;
  cursor: default;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.accent});
  color: ${props => props.theme.colors.text};
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1;

  &:hover {
    background: ${props => props.theme.colors.primaryGlow};
    border-color: ${props => props.theme.colors.primary};
    transform: scale(1.1);
    box-shadow: ${props => props.theme.shadows.glowPrimary};
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }
`;

const ModalBody = styled.div`
  padding: 1rem;
  max-height: 70vh;
  overflow-y: auto;
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  color: ${props => props.theme.colors.textDim};
`;

const EmptyCartIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 0.75rem;
  opacity: 0.5;
`;

const EmptyCartText = styled.h3`
  margin: 0 0 0.5rem 0;
  color: ${props => props.theme.colors.text};
`;

const EmptyCartSubtext = styled.p`
  margin: 0;
  font-size: 0.875rem;
`;

const CartItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const CartSummary = styled.div`
  border-top: 1px solid ${props => props.theme.colors.border};
  padding-top: 1rem;
  margin-top: 1rem;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const SummaryLabel = styled.span`
  color: ${props => props.theme.colors.textDim};
  font-size: 0.8rem;
`;

const SummaryValue = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-top: 2px solid ${props => props.theme.colors.border};
  margin-top: 1rem;
`;

const TotalLabel = styled.span`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
`;

const TotalValue = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: #059669;
`;

const FeeBreakdown = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: ${props => props.theme.colors.card};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.radii.md};
`;

const FeeBreakdownTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FeeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  font-size: 0.8rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const FeeLabel = styled.span`
  color: ${props => props.theme.colors.textDim};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FeeValue = styled.span`
  color: ${props => props.theme.colors.text};
  font-weight: 500;
`;

const FeeInfo = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.textDim};
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${props => props.theme.colors.border};
  line-height: 1.5;
`;

const SellerWarning = styled.div`
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 1rem;
  color: #92400e;
  font-size: 0.875rem;
  text-align: center;
`;

const CartActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 1rem;
  background: ${props => props.theme.colors.card};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.border};
`;

const ClearCartButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: #dc2626;
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus {
    outline: 2px solid #ef4444;
    outline-offset: 2px;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.5rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const ToggleButton = styled.button`
  flex: 1;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: ${props => props.$active ? '#10b981' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#6b7280'};
  
  &:hover {
    background: ${props => props.$active ? '#059669' : '#f3f4f6'};
    color: ${props => props.$active ? 'white' : '#374151'};
  }
`;

const CartModal = ({ onClose }) => {
  const navigate = useNavigate();
  const { 
    items, 
    totalItems, 
    totalPrice, 
    isEmpty, 
    isSingleSeller, 
    sellerInfo,
    getSellerGroups,
    removeFromCart,
    clearCart,
    isLoaded
  } = useCart();
  
  const [useMultiSellerView, setUseMultiSellerView] = useState(false);

  // Redirect guest to login when they click buy/order; close modal and send them back here after login
  const handleRequireLogin = () => {
    onClose();
    const redirect = encodeURIComponent(window.location.pathname || '/');
    navigate(`/login?redirect=${redirect}`);
  };

  const feeBreakdown = items.length === 0
    ? null
    : calculateFeeBreakdown(totalPrice, items[0].currency || 'USD');

  const handleCheckout = (items = null) => {
    // Close modal after checkout
    onClose();
  };

  const handleQuickCheckout = (sellerItems) => {
    // This will be handled by the CheckoutButtons component
    // The items are already filtered to the specific seller
    handleCheckout();
  };

  const handleSaveForLater = (sellerId) => {
    // Remove items from this seller from the cart
    // This could be enhanced to save to a "saved for later" list
    const sellerGroups = getSellerGroups();
    const targetGroup = sellerGroups.find(group => group.sellerId === sellerId);
    
    if (targetGroup) {
      targetGroup.items.forEach(item => {
        removeFromCart(item.productId);
      });
    }
  };

  const handleClearCart = () => {
    if (window.confirm('Are you sure you want to remove all items from your cart?')) {
      clearCart();
    }
  };

  const handleOverlayClick = (e) => {
    // Only close if clicking the overlay itself, not its children
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const modalContent = (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            Shopping Cart ({totalItems} {totalItems === 1 ? 'item' : 'items'})
          </ModalTitle>
          <CloseButton 
            onClick={onClose}
            title="Close cart (or press ESC)"
            aria-label="Close cart"
          >
            ×
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          {!isLoaded ? (
            <EmptyCart>
              <EmptyCartIcon>⏳</EmptyCartIcon>
              <EmptyCartText>Loading cart...</EmptyCartText>
              <EmptyCartSubtext>
                Please wait while we load your items
              </EmptyCartSubtext>
            </EmptyCart>
          ) : isEmpty ? (
            <EmptyCart>
              <EmptyCartIcon>🛒</EmptyCartIcon>
              <EmptyCartText>Your cart is empty</EmptyCartText>
              <EmptyCartSubtext>
                Add some products to get started!
              </EmptyCartSubtext>
            </EmptyCart>
          ) : (
            <>
              <CartActions>
                <div>
                  <strong>{totalItems} {totalItems === 1 ? 'item' : 'items'}</strong> in your cart
                </div>
                <ClearCartButton 
                  onClick={handleClearCart}
                  title="Remove all items from cart"
                  aria-label="Clear entire cart"
                >
                  🗑️ Clear Cart
                </ClearCartButton>
              </CartActions>

              {!isSingleSeller && (
                <>
                  <SellerWarning>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                      <strong>Multi-Seller Cart Detected</strong>
                    </div>
                    <div style={{ fontSize: '0.875rem', lineHeight: '1.4' }}>
                      Due to payment processing requirements, items from different sellers must be purchased separately. 
                      This ensures each seller receives their payment directly and securely.
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#6B7280', marginTop: '0.25rem' }}>
                      💡 <strong>Tip:</strong> You can checkout with one seller's items now and save others for later!
                    </div>
                  </SellerWarning>
                  
                  <ViewToggle>
                    <ToggleButton 
                      $active={!useMultiSellerView}
                      onClick={() => setUseMultiSellerView(false)}
                    >
                      📋 Grouped View
                    </ToggleButton>
                    <ToggleButton 
                      $active={useMultiSellerView}
                      onClick={() => setUseMultiSellerView(true)}
                    >
                      🛒 Checkout View
                    </ToggleButton>
                  </ViewToggle>
                </>
              )}
              
              {!isSingleSeller ? (
                useMultiSellerView ? (
                  // Show new multi-seller checkout component
                  <MultiSellerCheckout onCheckoutComplete={handleCheckout} onRequireLogin={handleRequireLogin} />
                ) : (
                  // Show seller groups for multi-seller carts (original view)
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {getSellerGroups().map((sellerGroup) => (
                      <SellerGroup
                        key={sellerGroup.sellerId}
                        sellerGroup={sellerGroup}
                        onCheckout={handleQuickCheckout}
                        onSaveForLater={handleSaveForLater}
                        onRequireLogin={handleRequireLogin}
                      />
                    ))}
                  </div>
                )
              ) : (
                // Show regular cart items for single seller
                <CartItems>
                  {items.map((item) => (
                    <CartItem key={item.productId} item={item} />
                  ))}
                </CartItems>
              )}
              
              <CartSummary>
                <SummaryRow>
                  <SummaryLabel>Items ({totalItems})</SummaryLabel>
                  <SummaryValue>
                    {formatPrice(totalPrice, items.length > 0 ? (items[0].currency || 'USD') : 'USD')}
                  </SummaryValue>
                </SummaryRow>
                
                {feeBreakdown && (
                  <FeeBreakdown>
                    <FeeBreakdownTitle>
                      💰 Fee Breakdown
                    </FeeBreakdownTitle>
                    <FeeRow>
                      <FeeLabel>Subtotal</FeeLabel>
                      <FeeValue>{formatPrice(feeBreakdown.subtotal, feeBreakdown.currency)}</FeeValue>
                    </FeeRow>
                    <FeeRow>
                      <FeeLabel>
                        Platform Fee ({feeBreakdown.platformFeePercentage}%)
                      </FeeLabel>
                      <FeeValue>{formatPrice(feeBreakdown.platformFee, feeBreakdown.currency)}</FeeValue>
                    </FeeRow>
                    <FeeRow>
                      <FeeLabel>
                        Payment Processing Fee (2.9% + {formatPrice(0.30, feeBreakdown.currency)})
                      </FeeLabel>
                      <FeeValue>{formatPrice(feeBreakdown.stripeFee, feeBreakdown.currency)}</FeeValue>
                    </FeeRow>
                    <FeeRow>
                      <FeeLabel>Creator Receives</FeeLabel>
                      <FeeValue style={{ color: '#059669', fontWeight: 600 }}>
                        {formatPrice(feeBreakdown.creatorReceives, feeBreakdown.currency)}
                      </FeeValue>
                    </FeeRow>
                    <FeeInfo>
                      <strong>Transparency Note:</strong> You pay the subtotal amount. Both the platform fee and payment processing fee are deducted from what the creator receives, not added to your total. This ensures full transparency about how fees are applied.
                    </FeeInfo>
                  </FeeBreakdown>
                )}
                
                <TotalRow>
                  <TotalLabel>Total You Pay</TotalLabel>
                  <TotalValue>
                    {formatPrice(totalPrice, items.length > 0 ? (items[0].currency || 'USD') : 'USD')}
                  </TotalValue>
                </TotalRow>

                {isSingleSeller && items.some(item => item.tipEnabled) && (
                  <div style={{ fontSize: '0.8rem', color: '#10b981', marginTop: '0.5rem', marginBottom: '0.25rem' }}>
                    Optional tip: choose amount below (min {formatPrice(1, items[0]?.currency || 'USD')} to cover fees).
                  </div>
                )}
                
                {isSingleSeller && (
                  <CheckoutButtons 
                    items={items} 
                    onCheckout={handleCheckout}
                    onRequireLogin={handleRequireLogin}
                  />
                )}
              </CartSummary>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );

  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : modalContent;
};

export default CartModal;
